import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import PageHeader from '../../components/ui/PageHeader';
import DialogHeader from '../../components/ui/DialogHeader';
import ActionToolbar from '../../components/ui/ActionToolbar';
import { useAuth } from '../../context/AuthContext';
import { VendedorEntity } from '../../entity/VendedorEntity';
import { VendedorService } from '../../service/VendedorService';
import { ReniecService } from '../../service/ReniecService';
import { filtrarDocumento, validarDocumento, maxLengthDocumento, placeholderDocumento } from '../../utils/documentoUtils';
import '../Usuario.css';

import { exportarDesdeDataTable } from '../../utils/excelUtils';

const Vendedores = () => {
    const { axiosInstance } = useAuth();
    const emptyVendedor = { ...VendedorEntity };

    const [vendedores, setVendedores] = useState([]);
    const [vendedor, setVendedor] = useState(emptyVendedor);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [selectedVendedores, setSelectedVendedores] = useState(null);
    const [saving, setSaving] = useState(false);
    const [usuarios, setUsuarios] = useState([]);

    const toast = useRef(null);
    const dt = useRef(null);

    const cargarVendedores = useCallback(async () => {
        try {
            const response = await VendedorService.listar(axiosInstance);
            setVendedores(response || []);
        } catch (error) {
            console.error(error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar los vendedores.', life: 3500 });
        }
    }, [axiosInstance]);

    const cargarUsuarios = useCallback(async () => {
        try {
            const response = await axiosInstance.get('usuario/', { params: { page: 0, size: 1000 } });
            const lista = response.data?.content || response.data || [];
            const mapped = lista.map(u => ({
                id: u.id,
                nombreCompleto: `${u.nombres || ''} ${u.apellidos || ''}`.trim() || u.username
            }));
            setUsuarios(mapped);
        } catch (error) {
            console.error(error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar los usuarios.', life: 3500 });
        }
    }, [axiosInstance]);

    useEffect(() => {
        cargarVendedores();
        cargarUsuarios();
    }, [cargarVendedores, cargarUsuarios]);

    const openNew = () => {
        setVendedor(emptyVendedor);
        setSubmitted(false);
        setDialogVisible(true);
    };

    const hideDialog = () => {
        setDialogVisible(false);
        setSubmitted(false);
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        if (name === 'numeroDocumento') {
            const filtered = filtrarDocumento(val, vendedor.tipoDocumento || 'DNI');
            setVendedor((prev) => ({ ...prev, [name]: filtered }));
        } else {
            setVendedor((prev) => ({ ...prev, [name]: val }));
        }
    };

    const onTipoDocumentoChange = (value) => {
        setVendedor((prev) => ({ ...prev, tipoDocumento: value, numeroDocumento: '' }));
    };

    const normalizeText = (value) => (value || '').trim();

    const mapReniecData = (data) => {
        if (!data) {
            return {};
        }
        const nombres = data.nombres || data.nombre || '';
        const apellidoPaterno = data.apellidoPaterno || data.apellido_paterno || '';
        const apellidoMaterno = data.apellidoMaterno || data.apellido_materno || '';
        const apellidos = data.apellidos || `${apellidoPaterno} ${apellidoMaterno}`.trim();
        return {
            nombres,
            apellidos
        };
    };

    const buscarDni = async () => {
        if (vendedor.tipoDocumento && vendedor.tipoDocumento !== 'DNI') {
            toast.current?.show({ severity: 'warn', summary: 'Validacion', detail: 'La consulta RENIEC aplica solo para DNI.', life: 3000 });
            return;
        }
        const dni = normalizeText(vendedor.numeroDocumento);
        if (dni.length !== 8) {
            toast.current?.show({ severity: 'warn', summary: 'Validacion', detail: 'El DNI debe tener 8 digitos.', life: 3000 });
            return;
        }

        try {
            const response = await ReniecService.consultarDNI(dni, axiosInstance);
            if (!response?.success) {
                const message = response?.message || 'No se encontraron datos para el DNI.';
                toast.current?.show({ severity: 'warn', summary: 'RENIEC', detail: message, life: 3500 });
                return;
            }

            const mapped = mapReniecData(response.data);
            setVendedor((prev) => ({ ...prev, ...mapped }));
            toast.current?.show({ severity: 'success', summary: 'RENIEC', detail: 'Datos cargados correctamente.', life: 3000 });
        } catch (error) {
            console.error(error);
            const detail = error?.response?.data?.message || error?.message || 'No se pudo consultar RENIEC.';
            toast.current?.show({ severity: 'error', summary: 'Error', detail, life: 4500 });
        }
    };

    const saveVendedor = async () => {
        setSubmitted(true);
        if (!vendedor.tipoDocumento || !vendedor.nombres) {
            return;
        }

        const docValidation = validarDocumento(vendedor.numeroDocumento, vendedor.tipoDocumento);
        if (!docValidation.valid) {
            toast.current?.show({ severity: 'warn', summary: 'Documento inválido', detail: docValidation.message, life: 3500 });
            return;
        }

        const payload = {
            ...vendedor,
            numeroDocumento: normalizeText(vendedor.numeroDocumento),
            nombres: normalizeText(vendedor.nombres),
            apellidos: normalizeText(vendedor.apellidos),
            telefono: normalizeText(vendedor.telefono),
            tipoDocumento: normalizeText(vendedor.tipoDocumento || 'DNI'),
            email: normalizeText(vendedor.email)
        };

        setSaving(true);
        try {
            if (vendedor.id) {
                await VendedorService.actualizar(vendedor.id, payload, axiosInstance);
                toast.current?.show({ severity: 'success', summary: 'Actualizado', detail: 'Vendedor actualizado correctamente.', life: 3000 });
            } else {
                await VendedorService.crear(payload, axiosInstance);
                toast.current?.show({ severity: 'success', summary: 'Creado', detail: 'Vendedor creado correctamente.', life: 3000 });
            }
            await cargarVendedores();
            setDialogVisible(false);
            setVendedor(emptyVendedor);
        } catch (error) {
            console.error(error);
            const detail = error?.response?.data?.message || 'No se pudo guardar el vendedor.';
            toast.current?.show({ severity: 'error', summary: 'Error', detail, life: 4500 });
        } finally {
            setSaving(false);
        }
    };

    const editVendedor = (rowData) => {
        setVendedor({ ...rowData });
        setSubmitted(false);
        setDialogVisible(true);
    };

    const confirmDeleteVendedor = (rowData) => {
        const nombreCompleto = `${rowData.nombres || ''} ${rowData.apellidos || ''}`.trim();
        confirmDialog({
            message: `¿Eliminar al vendedor "${nombreCompleto || 'sin nombre'}"?`,
            header: 'Confirmar Eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: 'Sí, eliminar',
            rejectLabel: 'No',
            accept: () => deleteVendedor(rowData)
        });
    };

    const deleteVendedor = async (rowData) => {
        try {
            await VendedorService.eliminar(rowData.id, axiosInstance);
            await cargarVendedores();
            toast.current?.show({ severity: 'success', summary: 'Eliminado', detail: 'Vendedor eliminado correctamente.', life: 3000 });
        } catch (error) {
            console.error(error);
            const detail = error?.response?.data?.message || 'No se pudo eliminar el vendedor.';
            toast.current?.show({ severity: 'error', summary: 'Error', detail, life: 4500 });
        }
    };

    const exportCSV = () => {
        if (dt.current) {
            exportarDesdeDataTable(dt.current, 'Vendedores');
        }
    };

    const indexBodyTemplate = (_, options) => (options.rowIndex ?? 0) + 1;

    const actionBodyTemplate = (rowData) => (
        <div className="action-buttons">
            <Button icon="pi pi-pencil" className="btn-edit" onClick={() => editVendedor(rowData)} tooltip="Editar" />
            <Button icon="pi pi-trash" className="btn-delete" onClick={() => confirmDeleteVendedor(rowData)} tooltip="Eliminar" />
        </div>
    );

    const dialogFooter = (
        <div className="dialog-footer-buttons">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={hideDialog} />
            <Button label="Guardar" icon="pi pi-check" onClick={saveVendedor} autoFocus loading={saving} />
        </div>
    );

    return (
        <div className="usuario-page">
            <div className="container">
                <PageHeader
                    title="Vendedores"
                    description="Administra los vendedores y asesores comerciales."
                    icon="pi pi-id-card"
                />

                <div className="main-content">
                    <div className="content-card">
                        <ConfirmDialog />
                        <Toast ref={toast} />

                        <ActionToolbar
                            onNew={openNew}
                            newLabel="Nuevo Vendedor"
                            onSearch={setGlobalFilter}
                            searchValue={globalFilter}
                            searchPlaceholder="Buscar vendedores..."
                            extraActions={
                                <Button
                                    icon="pi pi-download"
                                    tooltip="Exportar en Excel"
                                    tooltipOptions={{ position: 'bottom' }}
                                    className="btn-export"
                                    onClick={exportCSV}
                                />
                            }
                        />

                        <DataTable
                            ref={dt}
                            value={vendedores}
                            selection={selectedVendedores}
                            onSelectionChange={(e) => setSelectedVendedores(e.value)}
                            dataKey="id"
                            paginator
                            rows={10}
                            globalFilter={globalFilter}
                            globalFilterFields={['numeroDocumento', 'tipoDocumento', 'nombres', 'apellidos', 'telefono', 'email', 'jefeVentas.nombres', 'jefeVentas.apellidos']}
                            emptyMessage="No se encontraron vendedores."
                            exportFilename="Vendedores"
                        >
                            <Column header="N°" body={indexBodyTemplate} style={{ width: '80px', textAlign: 'center' }} />
                            <Column field="numeroDocumento" header="Documento" style={{ minWidth: '140px' }} />
                            <Column field="tipoDocumento" header="Tipo" style={{ minWidth: '120px' }} />
                            <Column field="nombres" header="Nombres" style={{ minWidth: '180px' }} />
                            <Column field="apellidos" header="Apellidos" style={{ minWidth: '180px' }} />
                            <Column field="telefono" header="Teléfono" style={{ minWidth: '140px' }} />
                            <Column field="email" header="Correo" style={{ minWidth: '200px' }} />
                            <Column header="Jefe de Ventas" body={(rowData) => {
                                if (!rowData.jefeVentas) return '-';
                                return `${rowData.jefeVentas.nombres || ''} ${rowData.jefeVentas.apellidos || ''}`.trim() || rowData.jefeVentas.username || '';
                            }} style={{ minWidth: '180px' }} />
                            <Column header="Acciones" body={actionBodyTemplate} style={{ minWidth: '140px', textAlign: 'center' }} />
                        </DataTable>
                    </div>
                </div>

                <Dialog
                    visible={dialogVisible}
                    style={{ width: '800px', maxWidth: '95vw' }}
                    header={
                        <DialogHeader
                            title={vendedor.id ? 'Editar Vendedor' : 'Nuevo Vendedor'}
                            subtitle={vendedor.id ? 'Modificar datos del vendedor' : 'Registrar un nuevo vendedor'}
                            icon="pi pi-id-card"
                        />
                    }
                    modal
                    className="p-fluid custom-profile-dialog"
                    footer={dialogFooter}
                    onHide={hideDialog}
                >
                    <div className="formgrid grid dialog-content-specific">
                        <div className="field col-12 md:col-6">
                            <label htmlFor="tipoDocumento">Tipo Documento</label>
                            <Dropdown
                                id="tipoDocumento"
                                value={vendedor.tipoDocumento || 'DNI'}
                                options={[
                                    { label: 'DNI', value: 'DNI' },
                                    { label: 'Carnet de Extranjeria', value: 'CE' },
                                    { label: 'RUC', value: 'RUC' }
                                ]}
                                onChange={(e) => onTipoDocumentoChange(e.value)}
                                placeholder="Seleccione tipo"
                                className="w-full"
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="numeroDocumento">Documento</label>
                            <div className="p-inputgroup">
                                <InputText
                                    id="numeroDocumento"
                                    value={vendedor.numeroDocumento}
                                    onChange={(e) => onInputChange(e, 'numeroDocumento')}
                                    required
                                    keyfilter="pint"
                                    maxLength={maxLengthDocumento(vendedor.tipoDocumento)}
                                    placeholder={placeholderDocumento(vendedor.tipoDocumento)}
                                    className={submitted && !vendedor.numeroDocumento ? 'p-invalid' : ''}
                                />
                                <Button icon="pi pi-search" className="p-button-outlined" type="button" onClick={buscarDni} />
                            </div>
                            {submitted && !vendedor.numeroDocumento && <small className="p-error">El documento es requerido.</small>}
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="telefono">Teléfono</label>
                            <InputText
                                id="telefono"
                                value={vendedor.telefono}
                                onChange={(e) => onInputChange(e, 'telefono')}
                                placeholder="Ingrese teléfono"
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="nombres">Nombres</label>
                            <InputText
                                id="nombres"
                                value={vendedor.nombres}
                                onChange={(e) => onInputChange(e, 'nombres')}
                                required
                                placeholder="Ingrese nombres"
                                className={submitted && !vendedor.nombres ? 'p-invalid' : ''}
                            />
                            {submitted && !vendedor.nombres && <small className="p-error">Los nombres son requeridos.</small>}
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="apellidos">Apellidos</label>
                            <InputText
                                id="apellidos"
                                value={vendedor.apellidos}
                                onChange={(e) => onInputChange(e, 'apellidos')}
                                placeholder="Ingrese apellidos"
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="email">Correo Electrónico</label>
                            <InputText
                                id="email"
                                value={vendedor.email}
                                onChange={(e) => onInputChange(e, 'email')}
                                placeholder="ejemplo@correo.com"
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="jefeVentas">Jefe de Ventas</label>
                            <Dropdown
                                id="jefeVentas"
                                value={vendedor.jefeVentas?.id || null}
                                options={usuarios}
                                optionLabel="nombreCompleto"
                                optionValue="id"
                                onChange={(e) => setVendedor((prev) => ({ ...prev, jefeVentas: e.value ? { id: e.value } : null }))}
                                placeholder="Seleccione jefe de ventas"
                                filter
                                showClear
                                className="w-full"
                            />
                        </div>
                    </div>
                </Dialog>
            </div>
        </div>
    );
};

export default Vendedores;
